import { useMemo } from 'react';
import { CUT_TYPES, serviceRowToCutType, totalTargetSec, type CutType } from '@busy/core';
import {
  useClientVisits,
  useClients,
  useCurrentUserProfile,
  useCustomServices,
  useLatestClientPhotoUrls,
} from '@busy/api';
import { Avatar, ModalCloseButton } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { aggregateClientTopPicks, matchesTopPick } from '../../lib/clientTopPicks';

// Service / cut type picker. Reached after the client check-in. Shows the picked
// client at the top, then the menu of available cut types. Tapping one starts the cut.
export function CutTypePicker() {
  const pickCutType = useCutTimer((s) => s.pickCutType);
  const backToCheckin = useCutTimer((s) => s.backToCheckin);
  const closeModal = useCutTimer((s) => s.closeModal);
  const pendingClient = useCutTimer((s) => s.pendingClient);
  const customServices = useCustomServices();
  const profile = useCurrentUserProfile();
  const photoUrls = useLatestClientPhotoUrls();
  // useClients() is a cache hit when checkin already mounted it. We
  // need it here so we can read the full client row (notes etc) for
  // the header; pendingClient only carries id/name/phone.
  const clients = useClients();
  // Per-client visit history feeds the "Most Requested" crown. Skipping
  // the fetch entirely for walk-ins (kind != 'existing') keeps the
  // picker fast for the no-client path.
  const clientId = pendingClient?.kind === 'existing' ? pendingClient.id : null;
  const clientVisits = useClientVisits(clientId ?? '');
  const t = useT();

  // Source-of-truth rule: once the editor has auto-seeded the built-in
  // CUT_TYPES into the user's customs (cut_types_seeded === true), the
  // customs list IS the menu — we don't append CUT_TYPES anymore, so any
  // deletion in Settings is reflected here. Before seeding (brand-new
  // account, hasn't opened the editor yet) we fall back to the built-ins
  // so the cold-start path still has a usable picker.
  const allCutTypes: CutType[] = useMemo(() => {
    const customs = (customServices.data ?? [])
      .filter((s) => !s.is_addon)
      .map(serviceRowToCutType);
    if (profile.data?.cut_types_seeded) return customs;
    return [...customs, ...CUT_TYPES];
  }, [customServices.data, profile.data?.cut_types_seeded]);

  // Most-requested service for this client (null on walk-ins / clients
  // with <2 visits — see clientTopPicks.MOST_REQUESTED_MIN_COUNT).
  const topPicks = useMemo(
    () => (clientId ? aggregateClientTopPicks(clientVisits.data ?? []) : null),
    [clientId, clientVisits.data],
  );

  // Full client row for the notes + (future) richer header data.
  const clientRow = useMemo(
    () =>
      clientId
        ? (clients.data ?? []).find((c) => c.id === clientId) ?? null
        : null,
    [clients.data, clientId],
  );

  const clientLabel =
    pendingClient?.kind === 'existing'
      ? `${pendingClient.firstName} ${pendingClient.lastName}`.trim() || t('cut.picker.client')
      : t('today.walkin');

  return (
    <>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <button
          type="button"
          onClick={backToCheckin}
          className="absolute top-4 left-4 text-white/70 hover:text-white inline-flex items-center gap-1 text-xs font-bold tracking-wide uppercase px-2 py-1 rounded hover:bg-white/10"
        >
          <ChevronLeft />
          {t('cut.back')}
        </button>

        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mt-2 mb-2">
          <ScissorsIcon className="w-3 h-3" />
          {t('cut.picker.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('cut.picker.title')}</h2>

        {/* Client confirmation row — reassures the barber the right person is checked in */}
        <div className="mt-3 inline-flex items-center gap-2 bg-white/8 rounded-full pl-1 pr-3 py-1">
          {pendingClient?.kind === 'existing' ? (
            <>
              <Avatar
                name={clientLabel}
                size="sm"
                photoUrl={
                  pendingClient?.kind === 'existing'
                    ? photoUrls.data?.[pendingClient.id]
                    : undefined
                }
              />
              <span className="text-sm font-semibold">{clientLabel}</span>
            </>
          ) : (
            <>
              <div className="w-6 h-6 rounded-full bg-white/15 grid place-items-center">
                <UserIcon className="w-3.5 h-3.5" />
              </div>
              <span className="text-sm font-semibold">{t('cut.picker.walkinAnon')}</span>
            </>
          )}
        </div>
        {/* Client notes — when on file. Sits directly below the name so
            the barber gets the "what does this person want?" cheat sheet
            BEFORE picking a service. The whitespace-pre-wrap preserves
            line breaks the barber typed during edit. Capped at three lines
            with line-clamp so a novella doesn't push the picker off
            screen; full text lives on the profile if they need it. */}
        {clientRow?.notes && (
          <div className="mt-2 flex items-start gap-1.5 text-[12px] leading-snug text-white/85 max-w-[640px]">
            <NoteIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gold-400" />
            <p className="whitespace-pre-wrap line-clamp-3">{clientRow.notes}</p>
          </div>
        )}
      </div>

      <div className="p-5 space-y-3">
        {allCutTypes.map((c) => {
          const totalMin = Math.round(totalTargetSec(c.phases as never[]) / 60);
          const phasesLine = c.phases.map((p) => (p.nameKey ? t(p.nameKey) : p.name)).join(' · ');
          const cutName = c.nameKey ? t(c.nameKey) : c.name;
          // "Most Requested" crown — when this client has had this
          // service 2+ times and it's their top one. matchesTopPick
          // compares service_id when available, name otherwise (covers
          // built-in CUT_TYPES whose id is a slug like 'classic-haircut'
          // and stored cut_type is the localized name).
          const isTopRequested = matchesTopPick(topPicks?.service ?? null, {
            id: c.id,
            name: c.name,
            resolvedName: cutName,
          });
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => pickCutType(c)}
              className={`w-full text-left rounded-xl border bg-surface p-4 transition-colors ${
                isTopRequested
                  ? 'border-gold-500 ring-1 ring-gold-500/40 hover:border-gold-400'
                  : 'border-line hover:border-navy-900 dark:hover:border-gold-500'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-bg-alt grid place-items-center text-navy-900 dark:text-gold-500 shrink-0">
                  <CutIcon icon={c.icon} className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="font-display text-lg">{cutName}</strong>
                    {isTopRequested && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gold-500 text-navy-900 text-[10px] font-extrabold uppercase tracking-wide">
                        <CrownIcon className="w-2.5 h-2.5" />
                        <span>{t('cut.picker.mostRequested')}</span>
                      </span>
                    )}
                    {c.priceCents > 0 && (
                      <span className="ml-auto bg-green-50 dark:bg-green-600/20 text-green-600 dark:text-green-300 text-[11px] font-extrabold px-2 py-0.5 rounded-full">
                        ${(c.priceCents / 100).toFixed(0)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-500 mt-0.5">
                    {t(
                      c.phases.length === 1
                        ? 'cut.picker.phaseCount_one'
                        : 'cut.picker.phaseCount_other',
                      { min: totalMin, phases: c.phases.length },
                    )}
                  </div>
                </div>
              </div>
              <div className="text-xs text-ink-500 mt-2 truncate pl-13">{phasesLine}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="7" r="4" />
      <path d="M5 21v-2a7 7 0 0 1 14 0v2" />
    </svg>
  );
}

function ScissorsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" />
      <line x1="14.47" y1="14.48" x2="20" y2="20" />
      <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" aria-hidden>
      <path d="M2 19h20l-2 2H4l-2-2z" />
      <path d="M3 7l4 5 5-8 5 8 4-5v10H3V7z" />
    </svg>
  );
}

function NoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  );
}

function CutIcon({ icon, className }: { icon: 'scissors' | 'kit' | 'razor' | 'baby' | 'bolt'; className?: string }) {
  if (icon === 'kit') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="7" width="18" height="13" rx="2" />
        <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      </svg>
    );
  }
  if (icon === 'razor') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v6" />
        <rect x="6" y="8" width="12" height="6" rx="1" />
        <path d="M8 14v6" />
        <path d="M16 14v6" />
      </svg>
    );
  }
  if (icon === 'baby') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12h.01" />
        <path d="M15 12h.01" />
        <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
        <circle cx="12" cy="12" r="10" />
      </svg>
    );
  }
  if (icon === 'bolt') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  return <ScissorsIcon className={className} />;
}
